import {
  Grid,
  Typography,
  Divider,
  Box,
  Chip,
  Paper,
  Button,
  TextField,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  InputAdornment,
  MenuItem,
  TablePagination,
  GlobalStyles,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useIntl } from "react-intl";
import ModalComponent from "components/Modal";
import { useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { numericFormatter } from "react-number-format";

import { useSelector } from "react-redux";
import { StoreType } from "../../types/genericTypes";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EditNoteIcon from "@mui/icons-material/EditNote";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AddBoxIcon from "@mui/icons-material/AddBox";
import SearchIcon from "@mui/icons-material/Search";

interface Ubicacion {
  id: number;
  id_producto: number;
  id_talla_brimagy: number;
  talla: string;
  estatus: number;
}

interface DetalleUbicacionProps {
  verUbicaciones?: Ubicacion[] | null;
  handleisAlertCrearUbicacionOpen: () => void;
  desactivarUbicacion: (datos: any) => Promise<void>;
  activarUbicacion: (datos: any) => Promise<void>;
  setVerEditarUbicacion: React.Dispatch<React.SetStateAction<any>>;
  handleisAlertOpenEditarUbicacion: () => void;
  ubicacionesPaginadas: Ubicacion[];
  page: number;
  rowsPerPage: number;
  handleChangePage: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  buscador: string;
  handleBuscadorChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const Ubicacion = ({
  verUbicaciones,
  handleisAlertCrearUbicacionOpen,
  desactivarUbicacion,
  activarUbicacion,
  setVerEditarUbicacion,
  handleisAlertOpenEditarUbicacion,
  ubicacionesPaginadas,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  buscador,
  handleBuscadorChange,
}: DetalleUbicacionProps) => {
  if (!verUbicaciones) return null;
  const intl = useIntl();

  const getEstado = (estado: number) => {
    switch (estado) {
      case 1:
        return "success";
      case 0:
        return "error";
      default:
        return "default";
    }
  };

  const estadosMap: { [key: number]: string } = {
    1: "ACTIVADO",
    0: "DESACTIVADO",
  };

  const traducirEstado = (estado: number | null): string => {
    if (estado === null || estado === undefined) return "SIN ESTADO";
    return estadosMap[estado] ?? "SIN ESTADO";
  };

  return (
    <Box sx={{ px: 2, pb: 2, mt: 3 }}>
      <Grid container spacing={3}>
        {/* Información de la talla */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Grid container>
              <Grid item xs={12} md={7}>
                <Typography
                  variant="h6"
                  color="primary"
                  gutterBottom
                  sx={{ color: "#004c93", textAlign: { xs: "center", sm: "left" } }}
                >
                  Ubicaciones
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                sm={3}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "stretch",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#041d45",
                      borderRadius: "10px 0 0 10px",
                      px: .5,
                      boxSizing: "border-box",
                    }}
                  >
                    <SearchIcon fontSize="medium" sx={{ color: "#fff" }} />
                  </Box>
                  <TextField
                    id="outlined-basic"
                    fullWidth
                    size="small"
                    label={intl.formatMessage({ id: "input_buscador" })}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0 10px 10px 0",
                      },
                      "& .MuiInputLabel-root": {
                        color: "#041d45",
                        fontWeight: 500,
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#041d45",
                      },
                      "& .MuiInputLabel-shrink": {
                        background: "#fff",
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "1px solid #041d45",
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        border: "1px solid #041d45",
                      },
                    }}
                    onChange={handleBuscadorChange}
                  />
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                md={2}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: { xs: "center", sm: "end" },
                  marginTop: { xs: 2, sm: 0 },
                }}
              >
                <Button
                  sx={{ color: "#fff", background: "#041d45" }}
                  variant="contained"
                  endIcon={<AddBoxIcon />}
                  onClick={(e: any) => {
                    handleisAlertCrearUbicacionOpen();
                  }}
                >
                  {intl.formatMessage({ id: "añadir_ubicacion" })}
                </Button>
              </Grid>
            </Grid>
            <Divider sx={{ m: 2 }} />
            {ubicacionesPaginadas.length > 0 ? (
              <>
                {ubicacionesPaginadas.map((c: any, key: number) => (
                  <>
                    <Grid container spacing={2} key={key}>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Almacén
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={
                            c.estatus === 0
                              ? {
                                color: "#7a7a7a",
                                textDecoration: "line-through",
                                textDecorationColor: "red",
                              }
                              : {}
                          }
                        >
                          {c?.nombre_almacen}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Clave
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={
                            c.estatus === 0
                              ? {
                                color: "#7a7a7a",
                                textDecoration: "line-through",
                                textDecorationColor: "red",
                              }
                              : {}
                          }
                        >
                          {c?.clave}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Nombre
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={
                            c.estatus === 0
                              ? {
                                color: "#7a7a7a",
                                textDecoration: "line-through",
                                textDecorationColor: "red",
                              }
                              : {}
                          }
                        >
                          {c?.nombre}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Descripción
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={
                            c.estatus === 0
                              ? {
                                color: "#7a7a7a",
                                textDecoration: "line-through",
                                textDecorationColor: "red",
                              }
                              : {}
                          }
                        >
                          {c?.descripcion}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Estatus
                        </Typography>
                        <Chip
                          label={traducirEstado(c?.estatus)}
                          color={getEstado(c?.estatus)}
                          sx={{ fontWeight: "bold", color: "#fff" }}
                        />
                      </Grid>
                      <Grid item xs={6} md={1}>
                        <Typography variant="body2" color="text.secondary">
                          Acciones
                        </Typography>
                        {c?.estatus === 1 && (
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => {
                                setVerEditarUbicacion(c);
                                handleisAlertOpenEditarUbicacion();
                              }}
                            >
                              <EditNoteIcon fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {c?.estatus === 1 ? (
                          <Tooltip title="Desactivar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => desactivarUbicacion({ id_ubicacion: c?.id_ubicacion })}
                            >
                              <DisabledByDefaultIcon fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Activar">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => activarUbicacion({ id_ubicacion: c?.id_ubicacion })}
                            >
                              <CheckCircleOutlineIcon fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Grid>
                    </Grid>
                    <Divider sx={{ m: 1 }} />
                  </>
                ))}

                <MDBox
                  mt={3}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    backgroundColor: "#fff",
                    justifyContent: "center",
                  }}
                >
                  <GlobalStyles
                    styles={{
                      ".MuiTablePagination-select.MuiSelect-standard": {
                        color: "#000 !important",
                        WebkitTextFillColor: "#000 !important",
                      },
                    }}
                  />
                  <TablePagination
                    component="div"
                    count={verUbicaciones?.length ?? 0}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[3, 6, 10, 15]}
                    labelRowsPerPage="Ubicaciones por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    sx={{
                      color: "#000",
                      direction: "ltr",
                      ".MuiTablePagination-toolbar": {
                        justifyContent: "space-between",
                        padding: "16px 24px",
                      },
                      ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                        margin: 0,
                      },
                      ".MuiTablePagination-actions svg": {
                        transform: "scaleX(-1)",
                      },
                    }}
                  />
                </MDBox>
              </>
            ) : (
              <Box
                sx={{
                  textAlign: "center",
                  py: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  {intl.formatMessage({ id: "no_hay_datos_registrados" })}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
export default Ubicacion;
